/**
 * src/routes/About.tsx
 *
 * Standalone /about page — same content as AboutModal but full-page layout.
 * Useful for direct linking and sharing. In-app "About" links open AboutModal
 * instead of navigating here, so the user stays on Home or Chat.
 */

import React from 'react';
import styled from 'styled-components';
import Footer from 'components/Footer';

/* ── Page shell ──────────────────────────────────────────────────── */

const Page = styled.main`
  min-height: 100vh;
  background: #fff;
  color: #0f172a;
  font-family: Helvetica, Arial, sans-serif;
`;

/* ── Hero ────────────────────────────────────────────────────────── */

const Hero = styled.section`
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const HeroInner = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 4rem 2rem 3.5rem;

  & > * + * { margin-top: 1.5rem; }
`;

const HeroContent = styled.div`
  max-width: 720px;
  & > * + * { margin-top: 1.25rem; }
`;

const Eyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #6b7280;
  margin: 0;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  color: #0f172a;
  line-height: 1.15;
  margin: 0;
`;

const HeroBody = styled.p`
  font-size: 1.125rem;
  line-height: 1.75;
  color: #374151;
  margin: 0;
`;

/* ── Content grid ────────────────────────────────────────────────── */

const ContentGrid = styled.section`
  max-width: 1024px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2.5rem;
  padding: 3.5rem 2rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  & > * + * { margin-top: 2rem; }
`;

const Aside = styled.aside`
  & > * + * { margin-top: 1.5rem; }
`;

const Card = styled.div`
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const CardLight = styled(Card)`
  background: #f8fafc;
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin: 0 0 1rem;
`;

const CardBody = styled.div`
  font-size: 1rem;
  line-height: 1.75;
  color: #374151;
  & > p { margin: 0; }
  & > p + p { margin-top: 0.75rem; }
`;

const TipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const TipCard = styled.div`
  border-radius: 16px;
  background: #fff;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
`;

const TipIcon = styled.img`
  width: 40px;
  height: 40px;
  margin-bottom: 0.65rem;
  display: block;
`;

const TipTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem;
`;

const TipBody = styled.p`
  font-size: 0.9rem;
  line-height: 1.55;
  color: #374151;
  margin: 0;
`;

const AsideTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.75rem;
`;

const GlanceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 1rem;
  line-height: 1.65;
  color: #374151;
  & > li + li { margin-top: 0.4rem; }
`;

const ResourceLinks = styled.div`
  & > * + * { margin-top: 0.75rem; }
`;

const ResourceLink = styled.a`
  display: block;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 0.875rem;
  text-decoration: none;
  color: inherit;
  font-size: 0.95rem;
  transition: background 0.15s;

  &:hover { background: #f8fafc; }

  & > span:first-child {
    display: block;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 0.15rem;
  }
`;

/* ── Video section ───────────────────────────────────────────────── */

const VideoSection = styled.section`
  background: rgb(55, 65, 81);
  color: #fff;
  border-top: 1px solid #e2e8f0;
`;

const VideoInner = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 3.5rem 2rem;
`;

const VideoTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 0.75rem;
`;

const VideoDesc = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #d1d5db;
  max-width: 640px;
  margin: 0;
`;

const VideoWrapper = styled.div`
  margin-top: 2rem;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #000;
  aspect-ratio: 16 / 9;

  & iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/* ─────────────────────────────────────────────────────────────────── */

export default function About() {
  return (
    <Page>
      <Hero>
        <HeroInner>
          <HeroContent>
            <Eyebrow>About This Interview</Eyebrow>
            <HeroTitle>A simple framework for noticing what fits</HeroTitle>
            <HeroBody>
              This experience is inspired by the idea of <em>ikigai</em>, often described as the
              intersection of what you love, what you are good at, what the world needs, and what
              you can be paid to do. We use that framework as a starting point for reflection, not
              as a test or a formula.
            </HeroBody>
            <HeroBody>
              For this version, we made one important change for younger users. Instead of asking
              about what you can be paid to do, we ask what you are recognized for. That makes the
              conversation more useful for students and emerging professionals who are still
              exploring school, activities, service, and early work experiences.
            </HeroBody>
          </HeroContent>
        </HeroInner>
      </Hero>

      <ContentGrid>
        <MainCol>
          <Card>
            <CardTitle>Why we use this approach</CardTitle>
            <CardBody>
              <p>
                Career exploration can feel abstract, especially when you are early in the process.
                This interview is designed to make it more concrete by asking about examples from
                your life: what pulls you in, what others notice about you, what you do well, and
                what kinds of contribution feel meaningful.
              </p>
              <p>
                The goal is not to identify one perfect answer. The goal is to notice patterns.
                Those patterns can help you name strengths, imagine possible paths, and generate a
                diagram you can revise as you learn more about yourself.
              </p>
            </CardBody>
          </Card>

          <CardLight>
            <CardTitle>How to use the interview well</CardTitle>
            <TipsGrid>
              <TipCard>
                <TipIcon src={`${process.env.PUBLIC_URL}/media/images/be-specific.svg`} alt="" />
                <TipTitle>Be specific</TipTitle>
                <TipBody>
                  Real examples from school, hobbies, family responsibilities, volunteer work,
                  teams, and projects lead to better suggestions.
                </TipBody>
              </TipCard>
              <TipCard>
                <TipIcon src={`${process.env.PUBLIC_URL}/media/images/think-patterns.svg`} alt="" />
                <TipTitle>Think in patterns</TipTitle>
                <TipBody>
                  You do not need a polished answer. The interview works best when you notice what
                  keeps showing up across different parts of your life.
                </TipBody>
              </TipCard>
              <TipCard>
                <TipIcon src={`${process.env.PUBLIC_URL}/media/images/use-voice.svg`} alt="" />
                <TipTitle>Use voice if helpful</TipTitle>
                <TipBody>
                  Some people think more clearly out loud. Voice mode is optional, but it can help
                  you respond more naturally and with more detail.
                </TipBody>
              </TipCard>
              <TipCard>
                <TipIcon src={`${process.env.PUBLIC_URL}/media/images/revise-grow.svg`} alt="" />
                <TipTitle>Revise as you grow</TipTitle>
                <TipBody>
                  Your diagram is a snapshot, not a verdict. You can revisit the interview later as
                  your interests and experiences evolve.
                </TipBody>
              </TipCard>
            </TipsGrid>
          </CardLight>
        </MainCol>

        <Aside>
          <Card>
            <AsideTitle>At a glance</AsideTitle>
            <GlanceList>
              <li>• Inspired by the ikigai framework</li>
              <li>• Adapted for younger users</li>
              <li>• Focused on reflection, not testing</li>
              <li>• Designed to create a revisable visual diagram</li>
            </GlanceList>
          </Card>

          <Card>
            <AsideTitle>Explore more</AsideTitle>
            <ResourceLinks>
              <ResourceLink
                href="https://medium.com/ideas-into-action/ikigai-the-perfect-career-diagnostic-3de932834be7"
                target="_blank"
                rel="noreferrer"
              >
                <span>Article</span>
                <span>Ikigai Explained</span>
              </ResourceLink>
              <ResourceLink
                href="https://positivepsychology.com/ikigai/"
                target="_blank"
                rel="noreferrer"
              >
                <span>Overview</span>
                <span>Philosophy and graphic</span>
              </ResourceLink>
              <ResourceLink
                href="https://www.youtube.com/watch?v=G2SqqjRn_c0"
                target="_blank"
                rel="noreferrer"
              >
                <span>Video</span>
                <span>Counseling from ikigai</span>
              </ResourceLink>
            </ResourceLinks>
          </Card>
        </Aside>
      </ContentGrid>

      <VideoSection>
        <VideoInner>
          <VideoTitle>Watch a short explainer</VideoTitle>
          <VideoDesc>
            This video offers one accessible entry point into the broader conversation around ikigai
            and how people use it to think about direction and purpose.
          </VideoDesc>
          <VideoWrapper>
            <iframe
              src="https://www.youtube.com/embed/G2SqqjRn_c0?si=5J7XecjHS3PLFQph"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </VideoWrapper>
        </VideoInner>
      </VideoSection>
      <Footer />
    </Page>
  );
}
